import { useMemo } from 'react';
import { Trash2 } from 'lucide-react';

import { useRegistries } from '@/react/portainer/registries/queries/useRegistries';
import { useUsers } from '@/portainer/users/queries/useUsers';
import { useTeams } from '@/react/portainer/users/teams/queries';
import { Option as AccessOption } from '@/react/portainer/access-control/AccessManagement/PorAccessManagementUsersSelector';

import { Alert } from '@@/Alert';
import { Button } from '@@/buttons';
import { Datatable } from '@@/datatables';
import { createPersistedStore } from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import type { Option as PortainerOption } from '@@/form-components/PortainerSelect';

import type { DockerRegistryFormData } from '../types';

import type { AccessRow } from './columns/helper';
import { useColumns } from './columns/useColumns';

const accessStorageKey = 'docker-registry-access-table';
const accessSettingsStore = createPersistedStore(accessStorageKey);

interface RegistryAccessDatatableProps {
  formData: { accessRows?: DockerRegistryFormData['accessRows'] };
  onChange: (
    key: 'accessRows',
    value: DockerRegistryFormData['accessRows']
  ) => void;
}

export function DockerRegistryAccessDatatable({
  formData,
  onChange,
}: RegistryAccessDatatableProps) {
  const accessRows = formData.accessRows || [];
  const { rowsWithNames, userTeamOptions, users, teams, isLoading, error } =
    useDockerRegistryAccessRows(accessRows);

  const tableState = useTableState(accessSettingsStore, accessStorageKey);

  const columns = useColumns({
    userTeamOptions,
    users,
    teams,
    handleAccessChange,
  });

  if (error) {
    return (
      <Alert color="error" title="Error loading data">
        {(error as Error)?.message ??
          'Unable to load registries, users, or teams'}
      </Alert>
    );
  }

  return (
    <Datatable
      title="Registry access list"
      getRowId={(row) => String(row.registryId)}
      dataset={rowsWithNames}
      columns={columns}
      settingsManager={tableState}
      isLoading={isLoading}
      isRowSelectable={() => true}
      renderTableActions={(selectedRows) => (
        <TableActions selectedRows={selectedRows} onRemove={handleRemoveRows} />
      )}
      data-cy="docker-registry-access-datatable"
      emptyContentLabel="No registry access configured"
      extendTableOptions={(opts) => ({ ...opts, autoResetPageIndex: false })}
    />
  );

  function handleRemoveRows(items: AccessRow[]) {
    const idsToRemove = new Set(items.map((item) => item.registryId));
    const newAccessRows = accessRows.filter(
      (row) => !idsToRemove.has(row.registryId)
    );
    onChange('accessRows', newAccessRows);
  }

  function handleAccessChange(
    item: AccessRow,
    selection: readonly AccessOption[]
  ) {
    const newAccessRows = [...accessRows];
    const index = newAccessRows.findIndex(
      (row) => row.registryId === item.registryId
    );
    if (index !== -1) {
      const userIds = selection
        .filter((o) => o.Type === 'user')
        .map((o) => o.Id);
      const teamIds = selection
        .filter((o) => o.Type === 'team')
        .map((o) => o.Id);
      newAccessRows[index] = { ...newAccessRows[index], userIds, teamIds };
      onChange('accessRows', newAccessRows);
    }
  }
}

function TableActions({
  selectedRows,
  onRemove,
}: {
  selectedRows: AccessRow[];
  onRemove: (items: AccessRow[]) => void;
}) {
  return (
    <Button
      color="dangerlight"
      size="small"
      icon={Trash2}
      onClick={() => onRemove(selectedRows)}
      data-cy="remove-registry-access-button"
      disabled={selectedRows.length === 0}
    >
      Remove
    </Button>
  );
}

function useDockerRegistryAccessRows(
  accessRows: DockerRegistryFormData['accessRows']
) {
  const registriesQuery = useRegistries({
    select: (registries) => registries.filter((r) => r.Id !== 0),
  });

  const usersQuery = useUsers(false, 0, true, (users) =>
    users.map((u) => ({
      Type: 'user' as const,
      Id: u.Id,
      Name: u.Username,
    }))
  );

  const teamsQuery = useTeams(false, 0, {
    select: (teams) =>
      teams.map((t) => ({
        Type: 'team' as const,
        Id: t.Id,
        Name: t.Name,
      })),
  });

  const users = usersQuery.data || [];
  const teams = teamsQuery.data || [];

  const userTeamOptions: PortainerOption<AccessOption>[] = useMemo(() => {
    const userOptions = usersQuery.data || [];
    const teamOptions = teamsQuery.data || [];
    return [...userOptions, ...teamOptions].map((v) => ({
      value: v,
      label: v.Name,
    }));
  }, [usersQuery.data, teamsQuery.data]);

  const rowsWithNames = useMemo<AccessRow[]>(() => {
    const registries = registriesQuery.data || [];
    return accessRows.map((row) => {
      const registry = registries.find((r) => r.Id === row.registryId);
      return {
        ...row,
        registryName: registry?.Name || 'Unknown',
      };
    });
  }, [accessRows, registriesQuery.data]);

  const isLoading =
    registriesQuery.isLoading || usersQuery.isLoading || teamsQuery.isLoading;

  const error = registriesQuery.error || usersQuery.error || teamsQuery.error;

  return {
    rowsWithNames,
    userTeamOptions,
    users,
    teams,
    isLoading,
    error,
  };
}
